import React, { useState, useEffect } from "react";
import { X, Smartphone, Lock, ShieldCheck } from "lucide-react";
import { Flame } from "lucide-react";
import { Rocket } from "lucide-react";
import { sendOtpUnlockCourse, verifyOtpUnlockCourse, createCourseOrder, verifyCoursePayment } from "../api/api";

const AccessPopup = ({ open, onClose, course }) => {
    const [phoneNumber, setPhoneNumber] = useState("");
    const [otp, setOtp] = useState(["", "", "", ""]);
    const [otpSent, setOtpSent] = useState(false);
    const [isLoading, setIsLoading] = useState(false);
    const [error, setError] = useState("");
    const [success, setSuccess] = useState("");
    const [resendTimer, setResendTimer] = useState(0);
    const [isVerified, setIsVerified] = useState(false);
    const [formData, setFormData] = useState({
        firstName: "",
        lastName: "",
        email: ""
    });
    const [countdown, setCountdown] = useState({
        days: "00",
        hours: "00",
        minutes: "00",
        seconds: "00"
    });

    useEffect(() => {
        const targetDate = new Date("2026-07-20T08:00:00+05:30");

        const timer = setInterval(() => {
            const now = new Date();
            const difference = targetDate - now;

            if (difference <= 0) {
                clearInterval(timer);
                setCountdown({
                    days: "00",
                    hours: "00",
                    minutes: "00",
                    seconds: "00"
                });
                return;
            }

            const days = Math.floor(difference / (1000 * 60 * 60 * 24));
            const hours = Math.floor((difference / (1000 * 60 * 60)) % 24);
            const minutes = Math.floor((difference / (1000 * 60)) % 60);
            const seconds = Math.floor((difference / 1000) % 60);

            setCountdown({
                days: String(days).padStart(2, "0"),
                hours: String(hours).padStart(2, "0"),
                minutes: String(minutes).padStart(2, "0"),
                seconds: String(seconds).padStart(2, "0")
            });
        }, 1000);

        return () => clearInterval(timer);
    }, []);

    useEffect(() => {
    }, [otpSent, isVerified]);

    const startResendTimer = (duration) => {
        setResendTimer(duration);
        const timer = setInterval(() => {
            setResendTimer((prev) => {
                if (prev <= 1) {
                    clearInterval(timer);
                    return 0;
                }
                return prev - 1;
            });
        }, 1000);
    };

    const handleOtpChange = (index, value) => {
        if (value.length <= 1 && /^\d*$/.test(value)) {
            const newOtp = [...otp];
            newOtp[index] = value;
            setOtp(newOtp);

            if (value && index < 3) {
                document.getElementById(`otp-${index + 1}`)?.focus();
            }
        }
    };

    const handleOtpKeyDown = (index, e) => {
        if (e.key === "Backspace" && !otp[index] && index > 0) {
            document.getElementById(`otp-${index - 1}`)?.focus();
        }
    };

    const handleSendOTP = async () => {
        if (phoneNumber.length < 10) {
            setError("Please enter a valid 10-digit phone number");
            return;
        }

        setIsLoading(true);
        setError("");
        setSuccess("");

        try {
            const response = await sendOtpUnlockCourse(phoneNumber, course?.name || "");

            if (response.status) {
                setOtpSent(true);
                setSuccess("OTP sent successfully!");
                startResendTimer(60);
                setError("");
                setOtp(["", "", "", ""]);
                setTimeout(() => document.getElementById('otp-0')?.focus(), 100);
            } else {
                setError(response.message || "Failed to send OTP");
            }
        } catch (error) {
            console.error("Send OTP error:", error);
            setError("Failed to send OTP. Please try again.");
        } finally {
            setIsLoading(false);
        }
    };

    const handleVerifyOTP = async () => {
        const otpString = otp.join("");
        if (otpString.length < 4) {
            setError("Please enter a valid 4-digit OTP");
            return;
        }

        setIsLoading(true);
        setError("");
        setSuccess("");

        try {
            const response = await verifyOtpUnlockCourse(phoneNumber, otpString);
            if (response.status) {
                setIsVerified(true);
                setSuccess("Phone number verified successfully!");
                setError("");
            } else {
                setError(response.message || "Invalid OTP. Please try again.");
            }
        } catch (error) {
            console.error("Verify OTP error:", error);
            setError("Failed to verify OTP. Please try again.");
        } finally {
            setIsLoading(false);
        }
    };

    const handleResendOTP = () => {
        if (resendTimer === 0) {
            handleSendOTP();
        }
    };

    const handleSubmit = async () => {
        // Validate form data
        if (!formData.firstName.trim()) {
            setError("Please enter your first name");
            return;
        }
        if (!formData.lastName.trim()) {
            setError("Please enter your last name");
            return;
        }
        if (!formData.email.trim()) {
            setError("Please enter your email address");
            return;
        }
        if (!formData.email.includes('@') || !formData.email.includes('.')) {
            setError("Please enter a valid email address");
            return;
        }

        try {
            setIsLoading(true);
            setError("");
            setSuccess("");

            // Create order with proper payload
            const orderPayload = {
                course_ids: [course?.id],
                coupon_code: "", // No coupon for access popup
                name: formData.firstName + " " + formData.lastName,
                email: formData.email
            };

            console.log("Order Payload:", orderPayload);

            const orderResponse = await createCourseOrder(orderPayload);
            console.log("Order Response:", orderResponse);

            if (!orderResponse.success) {
                setError(orderResponse.message || "Failed to create order");
                setIsLoading(false);
                return;
            }

            const order = orderResponse.order;
            
            // Initialize Razorpay
            const options = {
                key: order.razorpay_key || "rzp_test_YourKeyHere",
                amount: order.amount,
                currency: order.currency || "INR",
                name: "Nutrillion Academy",
                description: "Course Purchase",
                order_id: order.razorpay_order_id,
                prefill: {
                    name: formData.firstName + " " + formData.lastName,
                    email: formData.email,
                    contact: phoneNumber
                },
                theme: {
                    color: "#16a34a"
                },
                handler: async function (paymentResponse) {
                    try {
                        const verifyPayload = {
                            razorpay_order_id: paymentResponse.razorpay_order_id,
                            razorpay_payment_id: paymentResponse.razorpay_payment_id,
                            razorpay_signature: paymentResponse.razorpay_signature,
                            order_id: order.id
                        };

                        const verifyResponse = await verifyCoursePayment(verifyPayload);
                        
                        if (verifyResponse.success || verifyResponse.status) {
                            setSuccess("Payment Successful! You now have access to the course.");
                            setTimeout(() => {
                                onClose();
                            }, 3000);
                        } else {
                            setError(verifyResponse.message || "Payment verification failed");
                        }
                    } catch (error) {
                        console.error("Payment verification error:", error);
                        setError("Payment verification failed. Please contact support.");
                    }
                },
                modal: {
                    ondismiss: function() {
                        setIsLoading(false);
                    }
                }
            };

            // Load Razorpay script if not loaded
            if (!window.Razorpay) {
                const script = document.createElement("script");
                script.src = "https://checkout.razorpay.com/v1/checkout.js";
                script.onload = () => {
                    const razorpay = new window.Razorpay(options);
                    razorpay.open();
                };
                script.onerror = () => {
                    setError("Failed to load payment gateway. Please try again.");
                    setIsLoading(false);
                };
                document.body.appendChild(script);
            } else {
                const razorpay = new window.Razorpay(options);
                razorpay.open();
            }

        } catch (error) {
            console.error("Payment error:", error);
            setError(error.response?.data?.message || "Payment failed. Please try again.");
            setIsLoading(false);
        }
    };

    if (!open) return null;

    return (
        <div className="fixed font-lato inset-0 bg-black/40 flex items-center justify-center z-50 px-4">
            <div className="popup-scroll w-full md:max-w-[650px] max-w-[95%] bg-white rounded-lg shadow-xl relative max-h-[100vh] overflow-y-auto">
                <button
                    onClick={onClose}
                    className="absolute right-3 top-3 bg-white rounded-full w-6 h-6 text-gray-700 z-10 flex items-center justify-center hover:bg-gray-100 transition"
                >
                    <X size={16} />
                </button>

                <div className="bg-gradient-to-br from-[#003b25] to-[#092d2b] px-5 py-5 text-white">
                    <div className="flex items-center gap-1 text-[14px] tracking-wider text-green-400 font-bold">
                        <Rocket size={14} />
                        COMING SOON
                    </div>
                    <h2 className="mt-3 text-[22px] font-bold text-green-300">
                        Unlock First Access
                    </h2>
                    <p className="text-[14px] text-gray-300 mt-1 leading-4">
                        Be among the first to access Nutrillion Academy's flagship course.
                        Register now to receive launch updates and priority enrollment access.
                    </p>
                    <div className="mt-4 border border-white/10 bg-white/10 rounded-md p-2 flex items-center justify-between">
                        <span className="text-[14px] text-gray-300">LAUNCHING IN</span>
                        <div className="flex gap-2">
                            {[
                                [countdown.days, "DAYS"],
                                [countdown.hours, "HRS"],
                                [countdown.minutes, "MINS"],
                                [countdown.seconds, "SEC"]
                            ].map((x, i) =>
                                <div key={i} className="bg-white/10 rounded px-2 py-1 text-center">
                                    <div className="text-md font-bold">{x[0]}</div>
                                    <div className="text-[14px]">{x[1]}</div>
                                </div>
                            )}
                        </div>
                        <span className="text-[14px] bg-yellow-700/40 px-2 py-1 rounded-full flex items-center gap-1">
                            <Flame size={12} className="text-yellow-400" />
                            Limited Spots
                        </span>
                    </div>
                </div>

                <div className="p-4">
                    <div className="bg-green-50 border border-green-200 rounded-md p-3">
                        <div className="flex gap-2 items-center">
                            <div className="bg-green-600 text-white p-2 rounded">
                                <Smartphone size={15} />
                            </div>
                            <div>
                                <h4 className="text-[16px] font-bold text-green-700">
                                    Mobile Verification
                                </h4>
                                <p className="text-[14px] text-green-500">
                                    Secure your spot with OTP verification
                                </p>
                            </div>
                        </div>

                        {error && (
                            <div className="mt-2 bg-red-50 border border-red-200 text-red-700 p-2 rounded text-[12px]">
                                {error}
                            </div>
                        )}
                        {success && (
                            <div className="mt-2 bg-green-50 border border-green-200 text-green-700 p-2 rounded text-[12px]">
                                {success}
                            </div>
                        )}
                        <div className="flex mt-3 gap-1">
                            <div className="border rounded bg-white px-2 py-2 text-[14px]">
                                +91
                            </div>
                            <input
                                placeholder="Enter mobile number"
                                className="flex-1 border rounded px-2 text-[14px] outline-none focus:border-green-500 focus:ring-1 focus:ring-green-200"
                                value={phoneNumber}
                                onChange={(e) => setPhoneNumber(e.target.value.replace(/\D/g, ''))}
                                maxLength={10}
                                disabled={otpSent}
                            />
                            <button
                                onClick={handleSendOTP}
                                disabled={isLoading || otpSent || phoneNumber.length < 10}
                                className="bg-green-600 text-white rounded px-3 text-[14px] disabled:opacity-50 disabled:cursor-not-allowed whitespace-nowrap hover:bg-green-700 transition"
                            >
                                {isLoading ? "Sending..." : otpSent ? "OTP Sent ✓" : "Send OTP"}
                            </button>
                        </div>
                        {otpSent && (
                            <>
                                <p className="text-[12px] text-green-700 mt-2">
                                    Enter 4-digit OTP sent to +91 {phoneNumber}
                                </p>

                                <div className="flex justify-between mt-2">
                                    <div className="flex gap-2">
                                        {otp.map((digit, index) => (
                                            <input
                                                key={index}
                                                id={`otp-${index}`}
                                                type="text"
                                                maxLength="1"
                                                className="w-12 h-12 text-center text-[18px] font-bold bg-white border rounded-md focus:border-green-500 focus:ring-2 focus:ring-green-200 outline-none"
                                                value={digit}
                                                onChange={(e) => handleOtpChange(index, e.target.value)}
                                                onKeyDown={(e) => handleOtpKeyDown(index, e)}
                                                disabled={isVerified}
                                            />
                                        ))}
                                    </div>
                                    <button
                                        onClick={handleVerifyOTP}
                                        disabled={isLoading || isVerified}
                                        className="bg-green-600 text-white rounded px-4 text-[14px] disabled:opacity-50 disabled:cursor-not-allowed hover:bg-green-700 transition"
                                    >
                                        {isLoading ? "Verifying..." : isVerified ? "✓ Verified" : "Verify"}
                                    </button>
                                </div>

                                <div className="flex justify-between items-center mt-3">
                                    {isVerified ? (
                                        <span className="bg-green-100 text-green-700 rounded-full px-2 py-1 text-[12px]">
                                            ✓ Number Verified
                                        </span>
                                    ) : (
                                        <span className="text-[12px] text-gray-400">
                                            Didn't get it?
                                            {resendTimer > 0 ? (
                                                ` Resend in ${resendTimer}s`
                                            ) : (
                                                <button
                                                    onClick={handleResendOTP}
                                                    className="text-green-600 underline ml-1 hover:text-green-800"
                                                >
                                                    Resend OTP
                                                </button>
                                            )}
                                        </span>
                                    )}
                                </div>
                            </>
                        )}
                    </div>
                    <div className="grid grid-cols-2 gap-2 mt-4">
                        <input
                            placeholder="First name"
                            className="border rounded px-3 py-2 text-[14px] mt-4 disabled:bg-gray-100 disabled:cursor-not-allowed focus:border-green-500 focus:ring-1 focus:ring-green-200 outline-none"
                            value={formData.firstName}
                            onChange={(e) => setFormData({ ...formData, firstName: e.target.value })}
                            disabled={!isVerified}
                        />
                        <input
                            placeholder="Last name"
                            className="border rounded px-3 py-2 text-[14px] mt-4 disabled:bg-gray-100 disabled:cursor-not-allowed focus:border-green-500 focus:ring-1 focus:ring-green-200 outline-none"
                            value={formData.lastName}
                            onChange={(e) => setFormData({ ...formData, lastName: e.target.value })}
                            disabled={!isVerified}
                        />
                    </div>

                    <input
                        placeholder="Enter your email address"
                        className="border rounded px-3 py-2 text-[14px] w-full mt-4 disabled:bg-gray-100 disabled:cursor-not-allowed focus:border-green-500 focus:ring-1 focus:ring-green-200 outline-none"
                        value={formData.email}
                        onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                        disabled={!isVerified}
                    />

                    <div className="flex justify-between text-[12px] mt-5 text-gray-600">
                        <span>✓ Early Access</span>
                        <span>✓ No Spam</span>
                        <span>✓ Priority Launch Updates</span>
                    </div>

                    <button
                        onClick={handleSubmit}
                        disabled={!isVerified || isLoading}
                        className="mt-6 w-full bg-gradient-to-r from-green-600 to-teal-600 text-white rounded-md py-3 text-md font-semibold flex items-center justify-center gap-2 disabled:opacity-50 disabled:cursor-not-allowed hover:from-green-700 hover:to-teal-700 transition"
                    >
                        <Lock size={16} />
                        {isLoading ? "Processing..." : "Unlock First Access"}
                    </button>

                    <div className="mt-3 border border-orange-200 bg-orange-50 rounded p-2 text-center text-[12px] text-orange-700 flex items-center justify-center gap-1">
                        <Flame size={14} className="text-orange-600" />
                        Limited spots available for the founding batch — secure yours now.
                    </div>

                    <p className="text-center text-[12px] text-gray-400 mt-3">
                        🛡 100% Free · No credit card required · Unsubscribe anytime
                    </p>
                </div>
            </div>
        </div>
    );
};

export default AccessPopup;